import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const CATS = [
  { key: "", label: "All" },
  { key: "surface_protection", label: "Surface Protection" },
  { key: "coatings", label: "Coatings" },
  { key: "diy", label: "DIY" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";

  useEffect(() => {
    track("page_view", { page: "products", category });
    setLoading(true);
    const query = {};
    if (category) query.category = category;
    if (q) query.q = q;
    api.get("/products", { params: query }).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="products-page">
      <Seo title="Products" description="Browse ERRAH surface protection films, coatings and DIY kits." />
      <p className="errah-label gold-text mb-4">Catalogue</p>
      <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter">Protection products</h1>

      <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c.key} data-testid={`cat-${c.key || "all"}`}
              onClick={() => { const p = new URLSearchParams(params); c.key ? p.set("category", c.key) : p.delete("category"); setParams(p); }}
              className={`px-4 py-2 text-sm border transition-colors ${category === c.key ? "bg-[#C9A45C] text-black border-[#C9A45C]" : "border-white/15 text-gray-300 hover:border-white/40"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="product-search"
            className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm focus:border-[#C9A45C]" />
        </div>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><div className="w-6 h-6 border-2 border-[#C9A45C] border-t-transparent rounded-full animate-spin" /></div>
      ) : products.length === 0 ? (
        <p className="mt-16 text-gray-500">No products found.</p>
      ) : (
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <Link to={`/products/${p.id}`} data-testid={`product-card-${p.id}`} className="block group hair-border bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className="aspect-square overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <span className="errah-label">{p.protection_level}</span>
                  <h3 className="font-display text-base mt-1">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>
                  {p.price_from && <p className="mt-3 text-sm gold-text">From ₹{p.price_from}<span className="text-gray-600 text-xs">/{p.price_unit}</span></p>}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
